/**
 * Dictation Service
 *
 * API calls for voice dictation
 *
 * El audio se manda al backend de la clinica, no a un tercero. Antes esto no
 * existia: el navegador hablaba directamente con el reconocedor de Google.
 */

import { get, post, PROCESO_LARGO } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  DictationAvailability,
  TranscribeDictationData,
  TranscriptionResult,
} from '@/domain/dictation/types'

/**
 * ¿Se puede dictar ahora mismo?
 *
 * Detras de esta unica pregunta el servidor comprueba tres cosas: que la IPS
 * tenga el modulo, que el rol pueda dictar y que el motor este en pie.
 */
export async function getDictationAvailability(): Promise<DictationAvailability> {
  return get<DictationAvailability>(API_ROUTES.DICTATION.AVAILABILITY)
}

/**
 * Transcribe un trozo de audio.
 */
export async function transcribeDictation(
  data: TranscribeDictationData
): Promise<TranscriptionResult> {
  return post<TranscriptionResult, TranscribeDictationData>(
    API_ROUTES.DICTATION.TRANSCRIBE,
    data,
    PROCESO_LARGO
  )
}

/**
 * Formatos que puede grabar este navegador, en orden de preferencia.
 *
 * Opus es el que mejor comprime voz y lo graban Chromium y Firefox; Safari
 * solo da mp4. Se pregunta al navegador en vez de detectarlo por el nombre
 * del agente, que miente.
 */
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
]

/**
 * El primer formato que este navegador sepa grabar, o null si no sabe grabar.
 */
export function pickRecordingMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null

  return PREFERRED_MIME_TYPES.find((tipo) => MediaRecorder.isTypeSupported(tipo)) ?? null
}

/**
 * Lo que el navegador etiqueta al grabar trae el codec pegado
 * ("audio/webm;codecs=opus"). La API acepta el tipo a secas.
 */
export function baseMimeType(mimeType: string): string {
  return mimeType.split(';')[0]!.trim()
}

/**
 * Convierte el audio grabado a base64, que es como suben los archivos el
 * resto de pantallas de este proyecto.
 *
 * Se usa FileReader y no `btoa` sobre el buffer: un dictado de varios
 * segundos son cientos de miles de bytes, y pasarlos de uno en uno por
 * `String.fromCharCode` desborda la pila de llamadas.
 */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(new Error('No se pudo leer el audio grabado'))
    reader.onload = () => {
      const resultado = reader.result
      if (typeof resultado !== 'string') {
        reject(new Error('No se pudo leer el audio grabado'))
        return
      }

      // El data URL viene como "data:audio/webm;base64,AAAA...". Solo interesa
      // lo que va despues de la coma.
      const coma = resultado.indexOf(',')
      resolve(coma >= 0 ? resultado.slice(coma + 1) : resultado)
    }

    reader.readAsDataURL(blob)
  })
}
